import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Building2, Cable, ChevronDown, Plus, RotateCw } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import type { ITerminal } from "src/types/types";
import { S_CHANNEL_ITEM_POPUP, type TPaymentChannel } from "../channelList/channelData";
import { S_PAYMENT_CHANNEL_CREATE } from "../channelList/Channels";
import { AdminButton } from "../pages/administration/AdminControls";
import type { TIntegration } from "../pages/IntegrationsList";
import { CHANNELS_LIMIT, channelStatus, compareText, matchesQuery, methodTypeLabel, plural } from "./providersData";
import type { TPaymentProvider } from "./ProvidersList";
import {
	baseFont,
	CardBrandIcons,
	Cell,
	Chip,
	DirectoryCard,
	DirectoryRow,
	EmptyState,
	EntityCell,
	FilterChips,
	FilterSelect,
	LinkButton,
	MethodTile,
	Mono,
	Muted,
	Notice,
	RowChevron,
	SearchField,
	SkeletonRows,
	StatusPill,
	TabIntro,
	TabSurface,
	WarningText,
} from "./providersUi";

export type TChannelsPreset = { provider?: number; method?: number; integration?: number };

type TStatusFilter = "all" | "active" | "inactive" | "deleted" | "unconnected";

const Menu = styled.div`
	${baseFont};
	position: relative;
	.menu {
		position: absolute;
		top: calc(100% + 6px);
		right: 0;
		z-index: 20;
		width: 280px;
		max-height: 320px;
		overflow-y: auto;
		padding: 6px;
		border: 1px solid #d0d5dd;
		border-radius: 10px;
		background: #fff;
		box-shadow: 0 12px 16px -4px rgba(16, 24, 40, 0.08), 0 4px 6px -2px rgba(16, 24, 40, 0.03);
	}
	.menu-title {
		padding: 6px 10px 4px;
		color: #667085;
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.04em;
		text-transform: uppercase;
	}
	.menu button {
		display: flex;
		align-items: center;
		gap: 10px;
		width: 100%;
		padding: 8px 10px;
		border: 0;
		border-radius: 7px;
		background: transparent;
		color: #182230;
		font: inherit;
		text-align: left;
		cursor: pointer;
	}
	.menu button:hover,
	.menu button:focus-visible {
		background: #f2f4f7;
		outline: none;
	}
	.menu .muted {
		margin-left: auto;
		color: #98a2b3;
		font-size: 12px;
	}
	.menu .empty {
		padding: 10px;
		color: #667085;
	}
`;

/** Adding a channel needs its provider, so the button first asks which one. */
const AddChannelMenu = ({ providers, preferred }: { providers: TPaymentProvider[]; preferred?: TPaymentProvider }) => {
	useLocale();
	const [open, setOpen] = useState(false);
	const root = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!open) return;
		const onPointer = (event: MouseEvent) => {
			if (!root.current?.contains(event.target as Node)) setOpen(false);
		};
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") setOpen(false);
		};
		document.addEventListener("mousedown", onPointer);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onPointer);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);
	const create = (provider: TPaymentProvider) => {
		setOpen(false);
		S_PAYMENT_CHANNEL_CREATE.invoke({ id: 0, payment_provider_id: provider.id, provider_name: provider.name });
	};
	const sorted = [...providers].filter(provider => provider.status !== "deleted").sort((a, b) => compareText(a.name, b.name));

	if (preferred)
		return (
			<AdminButton variant="primarySmall" size="small" onClick={() => create(preferred)}>
				<Plus size={14} />{msg("addChannelTo")}{preferred.name}
			</AdminButton>
		);

	return (
		<Menu ref={root}>
			<AdminButton variant="primarySmall" size="small" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(!open)}>
				<Plus size={14} />{msg("addChannel_62f66a6a")}<ChevronDown size={14} />
			</AdminButton>
			{open && (
				<div className="menu" role="menu">
					<div className="menu-title">{msg("chooseAProvider")}</div>
					{sorted.length ? (
						sorted.map(provider => (
							<button key={provider.id} type="button" role="menuitem" onClick={() => create(provider)}>
								<Building2 size={15} color="#667085" />
								{provider.name}
								{provider.status !== "active" && <span className="muted">{provider.status}</span>}
							</button>
						))
					) : (
						<div className="empty">{msg("addAProviderFirst")}</div>
					)}
				</div>
			)}
		</Menu>
	);
};

export const ChannelsDirectory = ({
	channels,
	providers,
	methods,
	integrations,
	loading,
	busy,
	error,
	truncated,
	preset,
	onReload,
}: {
	channels: TPaymentChannel[];
	providers: TPaymentProvider[];
	methods: ITerminal[];
	integrations: TIntegration[];
	loading: boolean;
	busy: boolean;
	error?: IError;
	truncated: boolean;
	preset?: TChannelsPreset | null;
	onReload: () => void;
}) => {
	 useLocale();
	const [query, setQuery] = useState("");
	const [status, setStatus] = useState<TStatusFilter>("all");
	const [provider, setProvider] = useState(preset?.provider ? String(preset.provider) : "");
	const [method, setMethod] = useState(preset?.method ? String(preset.method) : "");
	const [integration, setIntegration] = useState(preset?.integration ? String(preset.integration) : "");

	const integrationOptions = useMemo(() => {
		const known = new Map<number, string>();
		for (const item of integrations) known.set(item.id, item.name);
		for (const channel of channels) if (channel.integration) known.set(channel.integration.id, channel.integration.name);
		return [...known.entries()].map(([value, label]) => ({ value: String(value), label })).sort((a, b) => compareText(a.label, b.label));
	}, [integrations, channels]);

	const scoped = channels
		.filter(channel => !provider || String(channel.provider?.id) === provider)
		.filter(channel => !method || String(channel.terminal?.id) === method)
		.filter(channel => !integration || String(channel.integration?.id) === integration)
		.filter(channel =>
			matchesQuery(query, [channel.name, channel.id, channel.currency, channel.provider?.name, channel.terminal?.name, channel.integration?.name, channel.integration?.type]),
		);
	const byStatus = (value: TStatusFilter) =>
		scoped.filter(channel => {
			const state = channelStatus(channel);
			if (value === "all") return true;
			if (value === "unconnected") return !channel.integration && state !== "deleted";
			return state === value;
		});
	const visible = byStatus(status).sort(
		(a, b) => compareText(a.provider?.name, b.provider?.name) || compareText(a.name, b.name),
	);
	const filtered = !!query || status !== "all" || !!provider || !!method || !!integration;
	const clear = () => {
		setQuery("");
		setStatus("all");
		setProvider("");
		setMethod("");
		setIntegration("");
	};
	const preferred = provider ? providers.find(item => String(item.id) === provider) : undefined;

	return (
		<TabSurface>
			<TabIntro
				text={msg("aChannelIsOneProviderPaymentMethodCurrencyOpenAChannelToConn_b7694b5e")}
				actions={
					<>
						<AdminButton variant="secondarySmall" size="small" disabled={busy} onClick={onReload}>
							<RotateCw size={14} />{msg("refresh")}</AdminButton>
						<AddChannelMenu providers={providers} preferred={preferred} />
					</>
				}
			/>
			{error && <Notice tone="error" action={<AdminButton variant="secondarySmall" size="small" onClick={onReload}>{msg("retry")}</AdminButton>}>{error.message}</Notice>}
			{truncated && (
				<Notice tone="warning">{msg("theServerReturnsAtMost")}{CHANNELS_LIMIT}{msg("channelsSoSomeMayBeMissingHereAProviderSOwnDialogListsItsCha_52408262")}</Notice>
			)}
			<DirectoryCard
				columns="minmax(240px, 1fr) minmax(90px, max-content) minmax(100px, max-content) minmax(64px, max-content) minmax(120px, max-content) minmax(110px, max-content)"
				head={["Channel", "Provider", "Method", "Currency", "Integration", "Status"]}
				toolbar={
					<>
						<SearchField value={query} onChange={setQuery} placeholder={msg("searchByNameIdCurrency")} />
						<FilterChips
							label={msg("status")}
							value={status}
							onChange={setStatus}
							options={[
								{ id: "all", label: msg("all"), count: scoped.length },
								{ id: "active", label: msg("active"), count: byStatus("active").length },
								{ id: "inactive", label: msg("inactive"), count: byStatus("inactive").length, hidden: !byStatus("inactive").length },
								{ id: "unconnected", label: msg("noIntegration"), count: byStatus("unconnected").length, warning: true, hidden: !byStatus("unconnected").length && status !== "unconnected" },
								{ id: "deleted", label: msg("deleted"), count: byStatus("deleted").length, hidden: !byStatus("deleted").length },
							]}
						/>
						<span className="grow" />
						<FilterSelect
							label={msg("allProviders")}
							value={provider}
							onChange={setProvider}
							options={[...providers].sort((a, b) => compareText(a.name, b.name)).map(item => ({ value: String(item.id), label: item.name }))}
						/>
						<FilterSelect
							label={msg("allMethods")}
							value={method}
							onChange={setMethod}
							options={[...methods].sort((a, b) => compareText(a.name, b.name)).map(item => ({ value: String(item.id), label: item.name }))}
						/>
						{integrationOptions.length > 0 && <FilterSelect label={msg("allIntegrations")} value={integration} onChange={setIntegration} options={integrationOptions} />}
					</>
				}
				footer={
					!loading &&
					channels.length > 0 && (
						<>
							<span>{filtered ? msg("templateOf_64f93023", { v0: visible.length, v1: plural(channels.length, "channel") }) : plural(channels.length, "channel")}</span>
							{filtered && <LinkButton onClick={clear}>{msg("clearFilters")}</LinkButton>}
						</>
					)
				}>
				{loading ? (
					<SkeletonRows rows={4} />
				) : !channels.length ? (
					<EmptyState
						icon={<Cable size={20} />}
						title={msg("noChannelsYet")}
						text={msg("useAddChannelAboveToCreateOneForAProviderThenConnectItToAnIn_1d8df119")}
					/>
				) : !visible.length ? (
					<EmptyState icon={<Cable size={20} />} title={msg("nothingMatchesTheFilters")} action={<LinkButton onClick={clear}>{msg("clearFilters")}</LinkButton>} />
				) : (
					visible.map(channel => {
						const state = channelStatus(channel);
						return (
							<DirectoryRow key={channel.id} label={msg("templateOpen_afaef5c3", { v0: channel.name })} muted={state === "deleted"} onOpen={() => S_CHANNEL_ITEM_POPUP.invoke(channel)}>
								<Cell kind="main">
									<EntityCell
										icon={<MethodTile type={channel.terminal?.type} title={methodTypeLabel(channel.terminal?.type)} />}
										name={channel.name || msg("templateChannel_e25c3617", { v0: channel.id })}
										sub={
											<>
												<Mono>#{channel.id}</Mono>
												<CardBrandIcons instrument={channel.instrument_type} />
											</>
										}
									/>
								</Cell>
								<Cell label={msg("provider")}>{channel.provider?.name ?? <Muted>—</Muted>}</Cell>
								<Cell label={msg("method")}>{channel.terminal?.name ?? <Muted>{methodTypeLabel(channel.terminal?.type)}</Muted>}</Cell>
								<Cell label={msg("currency")}>{channel.currency ? <Chip>{channel.currency}</Chip> : <Muted>—</Muted>}</Cell>
								<Cell label={msg("integration")}>
									{channel.integration ? (
										<div style={{ minWidth: 0 }}>
											<div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#182230" }}>{channel.integration.name}</div>
											<div style={{ color: "#667085", fontSize: 12 }}>{channel.integration.type}</div>
										</div>
									) : state === "deleted" ? (
										<Muted>—</Muted>
									) : (
										<WarningText>{msg("notConnected")}</WarningText>
									)}
								</Cell>
								<Cell kind="trail">
									<StatusPill status={state} />
									<RowChevron />
								</Cell>
							</DirectoryRow>
						);
					})
				)}
			</DirectoryCard>
		</TabSurface>
	);
};
